import { useEffect, useRef, useState } from "react";
import { Loader2, Mic, RotateCcw, Volume2, X } from "lucide-react";
import { api } from "@/lib/api";

// Say it after him: MiLu reads the line, you repeat it, he tells you how close you were.
export const MiluDrill = ({ lang, onClose }) => {
  const [lines, setLines] = useState(null);
  const [at, setAt] = useState(0);
  const [state, setState] = useState("idle");   // idle | speaking | listening | checking
  const [mark, setMark] = useState(null);
  const gear = useRef({});

  useEffect(() => {
    api.get("/wingman/drill", { params: { lang } })
      .then(({ data }) => setLines(data.lines || []))
      .catch(() => setLines([]));
    return () => {
      gear.current.audio?.pause();
      gear.current.stream?.getTracks().forEach((t) => t.stop());
      if (gear.current.recorder?.state === "recording") gear.current.recorder.stop();
    };
  }, [lang]);

  const line = lines?.[at] || "";

  const hearIt = async () => {
    if (!line) return;
    setState("speaking");
    try {
      const spoken = await api.post("/wingman/speak", { text: line, voice: "nova" },
                                    { responseType: "blob", timeout: 60000 });
      const audio = new Audio(URL.createObjectURL(spoken.data));
      gear.current.audio = audio;
      audio.onended = () => setState("idle");
      audio.onerror = () => setState("idle");
      await audio.play();
    } catch {
      setState("idle");
    }
  };

  const sayIt = async () => {
    if (!line || state === "listening") return;
    setMark(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      gear.current = { ...gear.current, stream, recorder };
      const bits = [];
      recorder.ondataavailable = (e) => e.data.size && bits.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setState("checking");
        try {
          const form = new FormData();
          form.append("audio", new Blob(bits, { type: "audio/webm" }), "drill.webm");
          const { data: heard } = await api.post("/wingman/listen", form, { timeout: 60000 });
          const { data } = await api.post("/wingman/drill/check",
                                          { said: heard.text || "", target: line });
          setMark({ ...data, heard: heard.text || "" });
        } catch {
          setMark({ score: 0, verdict: "That did not come through — try again.", missed: [] });
        }
        setState("idle");
      };
      recorder.start();
      setState("listening");
      setTimeout(() => recorder.state === "recording" && recorder.stop(), 6000);
    } catch {
      setMark({ score: 0, verdict: "MiLu needs the microphone to hear you.", missed: [] });
      setState("idle");
    }
  };

  return (
    <div data-testid="milu-drill"
         className="mx-5 mb-2 shrink-0 rounded-2xl border border-brand/30 bg-panel/80 px-3 py-2.5">
      <div className="flex items-center gap-2">
        <p className="text-[11px] uppercase tracking-widest text-brand">Say it after me</p>
        {lines?.length > 0 && (
          <span className="text-[10px] text-ink-soft">{at + 1} of {lines.length}</span>
        )}
        <button data-testid="milu-drill-close" onClick={onClose} aria-label="Close drill"
                className="ms-auto text-ink-soft transition-colors hover:text-ink">
          <X className="h-3 w-3" />
        </button>
      </div>

      {lines === null && (
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-ink-soft">
          <Loader2 className="h-3 w-3 animate-spin" /> Finding your slips…
        </p>
      )}
      {lines?.length === 0 && (
        <p data-testid="milu-drill-empty" className="mt-2 text-[11px] text-ink-soft">
          Nothing to drill yet — have a practice call first and I will keep your corrections.
        </p>
      )}

      {line && (
        <>
          <p data-testid="milu-drill-line" dir="auto" className="mt-2 text-sm text-ink">{line}</p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <button
              data-testid="milu-drill-hear"
              onClick={hearIt}
              disabled={state === "speaking"}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-2.5 py-1 text-[11px] text-ink-soft transition-colors hover:border-brand/50 hover:text-brand disabled:opacity-50"
            >
              <Volume2 className="h-3 w-3" /> {state === "speaking" ? "Reading…" : "Hear it"}
            </button>
            <button
              data-testid="milu-drill-say"
              onClick={sayIt}
              disabled={state === "listening" || state === "checking"}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                state === "listening" ? "border-pink-400 bg-pink-500/15 text-pink-300"
                  : "border-brand/50 text-brand hover:bg-brand/10"}`}
            >
              {state === "checking"
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Mic className="h-3 w-3" />}
              {state === "listening" ? "Listening…" : state === "checking" ? "Checking…" : "Say it"}
            </button>
            {lines.length > 1 && (
              <button
                data-testid="milu-drill-next"
                onClick={() => {
                  setMark(null);
                  setAt((i) => (i + 1) % lines.length);
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-2.5 py-1 text-[11px] text-ink-soft transition-colors hover:text-ink"
              >
                <RotateCcw className="h-3 w-3" /> Next line
              </button>
            )}
          </div>
        </>
      )}

      {mark && (
        <div data-testid="milu-drill-mark" className="mt-2 rounded-xl bg-white/5 px-2.5 py-2">
          <p className="text-[11px] text-ink">
            <span data-testid="milu-drill-score"
                  className={`me-1.5 font-semibold ${mark.score >= 75 ? "text-emerald-300" : "text-amber-300"}`}>
              {mark.score}%
            </span>
            {mark.verdict}
          </p>
          {mark.heard && (
            <p dir="auto" className="mt-1 text-[10px] text-ink-soft">I heard: “{mark.heard}”</p>
          )}
          {mark.missed?.length > 0 && (
            <p className="mt-1 text-[10px] text-ink-soft">Watch: {mark.missed.join(", ")}</p>
          )}
        </div>
      )}
    </div>
  );
};
